import {
  QBankStructureStatus,
  QuestionStatus,
  useExportQuestionItemAnalysisRecordsLazyQuery,
  useGetAllQBankStructuresQuery,
} from '@report/graphql';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Flex,
  Text,
} from '@shared/components';
import { useSnackbar } from '@shared/snackbar';
import { dayjs, toDayRange, triggerFileDownload } from '@shared/utils';

import { useItemAnalysisSearchParams } from '../../hooks';

const MAXIMUM_EXPORT_COUNT = 50000;

interface ItemAnalysisReportExportModalProps {
  open: boolean;
  onClose: () => void;
  totalCount: number;
}
const QuestionStatusLabelMapping: Record<QuestionStatus, string> = {
  [QuestionStatus.Published]: 'Published',
  [QuestionStatus.Disabled]: 'Disabled',
};

export default function ItemAnalysisReportExportModal(
  props: Readonly<ItemAnalysisReportExportModalProps>,
) {
  const { open, onClose, totalCount } = props;
  const { showSnackbar } = useSnackbar();

  const [
    {
      startDate,
      endDate,
      examCourse,
      questionStatus,
      minExaminees,
      knowledgeGroupSelection,
      knowledgeGroupCustomThreshold,
      nonKnowledgeGroupSelection,
      nonKnowledgeGroupCustomThreshold,
      pBufferEasyThreshold,
      pBufferMediumThreshold,
      pBufferHardThreshold,
    },
  ] = useItemAnalysisSearchParams();

  const formattedStartDate = startDate ? dayjs(startDate).format('D MMM BBBB') : '-';
  const formattedEndDate = endDate ? dayjs(endDate).format('D MMM BBBB') : '-';

  const [exportQuestionItemAnalysisRecords, { loading: isExporting }] =
    useExportQuestionItemAnalysisRecordsLazyQuery();

  const { data: qBankStructuresData } = useGetAllQBankStructuresQuery({
    variables: {
      getAllQBankStructuresInput: {
        statuses: [
          QBankStructureStatus.Published,
          QBankStructureStatus.Unpublished,
          QBankStructureStatus.Archived,
        ],
      },
    },
  });

  const selectedQBankStructure =
    qBankStructuresData?.reportGetAllQBankStructures?.qBankStructures.find((structure) =>
      examCourse?.includes(structure.id),
    );
  const handleExport = async () => {
    const { startedAt, endedAt } = toDayRange(startDate, endDate);
    await exportQuestionItemAnalysisRecords({
      variables: {
        exportQuestionItemAnalysisRecordsInput: {
          startAt: startedAt ?? new Date(),
          questionStatus: questionStatus,
          qBankStructureId: examCourse ?? '',

          nonKnowledgeGroup: {
            value: nonKnowledgeGroupCustomThreshold ?? undefined,
            type: nonKnowledgeGroupSelection ?? null,
          },
          pValue: {
            hard: {
              min: 0,
              max: pBufferHardThreshold,
            },
            medium: {
              min: Number(pBufferHardThreshold + 0.01),
              max: pBufferMediumThreshold,
            },
            easy: {
              min: Number(pBufferMediumThreshold + 0.01),
              max: pBufferEasyThreshold,
            },
          },
          minimumExamineePerQuestion: minExaminees ?? 0,
          knowledgeGroup: {
            value: knowledgeGroupCustomThreshold ?? undefined,
            type: knowledgeGroupSelection ?? null,
          },
          endAt: endedAt ?? new Date(),
        },
      },
      fetchPolicy: 'network-only',
      onCompleted: (data) => {
        const signedUrl = data.reportExportQuestionItemAnalysisRecords.signedUrl;
        if (!signedUrl) {
          showSnackbar({
            message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
            variant: 'error',
          });
          onClose();
          return;
        }

        triggerFileDownload(signedUrl);
        showSnackbar({
          message: 'ส่งออกข้อมูลเรียบร้อยแล้ว',
          variant: 'success',
        });
        onClose();
      },
      onError: () => {
        showSnackbar({
          message: 'ส่งออกข้อมูลไม่สำเร็จ กรุณาลองใหม่อีกครั้ง',
          variant: 'error',
        });
        onClose();
      },
    });
  };

  const isExceedingMaximumExportCount = totalCount > MAXIMUM_EXPORT_COUNT;
  const disableExport = isExporting || isExceedingMaximumExportCount;

  return (
    <Dialog open={open} onClose={onClose} maxWidth='xs'>
      <DialogTitle onClose={onClose} loading={isExporting}>
        ยืนยันการส่งออกข้อมูล
      </DialogTitle>
      <DialogContent dividers>
        <Flex sx={(theme) => ({ flexDirection: 'column', gap: theme.spacing(3) })}>
          {isExceedingMaximumExportCount ? (
            <Alert severity='error'>
              จำกัดการดาวน์โหลดสูงสุด {MAXIMUM_EXPORT_COUNT.toLocaleString('th-TH')} รายการต่อครั้ง
            </Alert>
          ) : null}
          <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              Date:
            </Text>
            <Text>
              {formattedStartDate} - {formattedEndDate}
            </Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              คลังข้อสอบ:
            </Text>
            <Text>{selectedQBankStructure?.name}</Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              สถานะ:
            </Text>
            <Text>
              {questionStatus.map((status) => QuestionStatusLabelMapping[status]).join(', ')}
            </Text>
          </Flex>
          <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
            <Text variant='body1Semibold' sx={{ flexBasis: '100px', flexShrink: 0 }}>
              รวมทั้งหมด:
            </Text>
            <Text>{totalCount.toLocaleString('th-TH')} รายการ</Text>
          </Flex>
        </Flex>
      </DialogContent>
      <DialogActions>
        <Flex sx={(theme) => ({ gap: theme.spacing(2), justifyContent: 'flex-end' })}>
          <Button size='large' variant='outlined' color='secondary' onClick={onClose}>
            ยกเลิก
          </Button>
          <Button
            size='large'
            loading={isExporting}
            disabled={disableExport}
            onClick={handleExport}
          >
            ส่งออกข้อมูล
          </Button>
        </Flex>
      </DialogActions>
    </Dialog>
  );
}
